import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { progressService } from '@/services/progressService'
import type { UpdateProgressRequest } from '@/types/enrollment'

export function useProgress(enrollmentId: string) {
  return useQuery({
    queryKey: ['progress', enrollmentId],
    queryFn: async () => {
      const response = await progressService.getProgress(enrollmentId)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch progress')
      }
      return response.data
    },
    enabled: !!enrollmentId,
  })
}

export function useEnrollmentProgress(enrollmentId: string) {
  return useQuery({
    queryKey: ['progress', 'enrollment', enrollmentId],
    queryFn: async () => {
      const response = await progressService.getByEnrollment(enrollmentId)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch enrollment progress')
      }
      return response.data
    },
    enabled: !!enrollmentId,
  })
}

export function useOverallProgress(enrollmentId: string) {
  return useQuery({
    queryKey: ['progress', 'summary', enrollmentId],
    queryFn: async () => {
      const response = await progressService.getOverallProgress(enrollmentId)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch overall progress')
      }
      return response.data
    },
    enabled: !!enrollmentId,
  })
}

export function useStartLesson() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ enrollmentId, lessonId }: { enrollmentId: string; lessonId: string }) =>
      progressService.startLesson(enrollmentId, lessonId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['progress', variables.enrollmentId] })
      queryClient.invalidateQueries({ queryKey: ['progress', 'enrollment', variables.enrollmentId] })
      queryClient.invalidateQueries({ queryKey: ['progress', 'summary', variables.enrollmentId] })
      // Invalidate all enrollment queries (with any filters/params)
      queryClient.invalidateQueries({ queryKey: ['enrollments'], exact: false })
    },
  })
}

export function useCompleteLesson() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      enrollmentId,
      lessonId,
      data,
    }: {
      enrollmentId: string
      lessonId: string
      data: UpdateProgressRequest
    }) => progressService.completeLesson(enrollmentId, lessonId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['progress', variables.enrollmentId] })
      queryClient.invalidateQueries({ queryKey: ['progress', 'enrollment', variables.enrollmentId] })
      queryClient.invalidateQueries({ queryKey: ['progress', 'summary', variables.enrollmentId] })
      // Invalidate all enrollment queries (with any filters/params)
      queryClient.invalidateQueries({ queryKey: ['enrollments'], exact: false })
    },
  })
}

export function useMarkLessonComplete() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ lessonId, enrollmentId }: { lessonId: string; enrollmentId: string }) =>
      progressService.markLessonComplete(lessonId, enrollmentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['progress', variables.enrollmentId] })
      queryClient.invalidateQueries({ queryKey: ['progress', 'enrollment', variables.enrollmentId] })
      queryClient.invalidateQueries({ queryKey: ['progress', 'summary', variables.enrollmentId] })
      // Invalidate all enrollment queries (with any filters/params)
      queryClient.invalidateQueries({ queryKey: ['enrollments'], exact: false })
    },
  })
}

export function useSubmitQuizAttempt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      quizId,
      answers,
    }: {
      enrollmentId: string
      quizId: string
      answers: Array<{ questionId: string; answer: string | string[] }>
    }) => progressService.submitQuizAttempt(quizId, answers),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['progress', variables.enrollmentId] })
      queryClient.invalidateQueries({ queryKey: ['progress', 'enrollment', variables.enrollmentId] })
      queryClient.invalidateQueries({ queryKey: ['progress', 'summary', variables.enrollmentId] })
      // Invalidate all enrollment queries (with any filters/params)
      queryClient.invalidateQueries({ queryKey: ['enrollments'], exact: false })
    },
  })
}
